import { v4 as uuid } from "uuid";
import config from "config/config";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import { customerChatMainCategoriesAnnuity } from "js/sfdc";
import { getAgentPrechatInfo } from "./utils/getAgentPrechatInfo";
import { getCustomerPrechatInfo } from "./utils/getCustomerPrechatInfo";
import {
  getImage,
  getProductLineByPolicyType,
  isAgentPortal,
} from "js/utilities";
import { getPolicyType, getName, getPoliciesOptions } from "js/formatting";
import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import { getPrimaryOwnerFromParties } from "js/utilities";

import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";

export default function ChatAuthedFormWindow({
  user,
  policies,
  mainCategories,
  subCategories,
  setHideCustomChat,
  connectingToSF,
  setConnectingToSF,
  openChatWindow,
  setOpenChatWindow,
}) {
  const { t } = useTranslation();
  const UUID = uuid();
  const [selectedMainCategory, setSelectedMainCategory] = useState("all");
  const [selectedSubCategory, setSelectedSubCategory] = useState("all");
  const [formStep, setFormStep] = useState(1);

  const [SFButtonId, setSFButtonId] = useState(mainCategories[0].buttonId);
  const [SFDCName, setSFDCName] = useState(mainCategories[0].sfdcName);
  const [SFDCESWLiveAgentDevName, setSFDCESWLiveAgentDevName] = useState(
    mainCategories[0].sfdcESWLiveAgentDevName
  );

  // default a empty value to fail validation on purpose
  const [selectedPolicyId, setSelectedPolicyId] = useState({
    value: "all",
    displayAs: `${t("AutoComplate-select-your-contract")}`,
    selectedPolicyDisplayAs: `${t("AutoComplate-select-your-contract")}`,
  });

  const [policySelection, setPolicySelection] = useState([]);
  const RESTRICTED_PRODUCT_TYPES = ["LZ", "LQ", "AVD"];

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    clearErrors,
    reset,
    control,
    setError,
    formState: { errors },
    watch,
  } = useForm();

  const policyNumber = watch("policyNumber");

  const parsePolicy = (value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const isRestrictedPolicy = (policy) =>
    policy?.rawProductType &&
    RESTRICTED_PRODUCT_TYPES.includes(policy.rawProductType?.toUpperCase());

  const initESW = (gslbBaseURL, prechatContext) => {
    const { getValueByLabel, query, productType, routingQueueName } =
      prechatContext;
    window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
    window.embeddedservice_bootstrap.settings.language = "en_US";

    if (config.SFDC_VERSION === "PROD") {
      window.embeddedservice_bootstrap.init(
        "00Df400000488Mv",
        "EVL_Chat_Support",
        "https://nttfsiclaims.my.site.com/ESWEVLChatSupport1770287966136",
        {
          scrt2URL: "https://nttfsiclaims.my.salesforce-scrt.com",
        }
      );
    } else if (config.SFDC_VERSION === "UAT") {
      // UAT
      window.embeddedservice_bootstrap.init(
        "00DcZ000000rXkE",
        "EVL_Chat_Support",
        "https://nttfsiclaims--nttfsiuat.sandbox.my.site.com/ESWEVLChatSupport1769583709310",
        {
          scrt2URL:
            "https://nttfsiclaims--nttfsiuat.sandbox.my.salesforce-scrt.com",
        }
      );
    } else if (config.SFDC_VERSION === "MO") {
      // MO
      window.embeddedservice_bootstrap.init(
        "00DTI000004vKir",
        "EVL_Chat_Agent_Channel_New",
        "https://nttfsiclaims--fsitagqa.sandbox.my.site.com/ESWEVLChatAgentChannel1768226837707",
        {
          scrt2URL:
            "https://nttfsiclaims--fsitagqa.sandbox.my.salesforce-scrt.com",
        }
      );
    }

    window.addEventListener("onEmbeddedMessagingReady", () => {
      window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
        Description: getValueByLabel("Description") || "",
        CarrierCode: getValueByLabel("Carrier Code") || "",
        CarrierName: getValueByLabel("Carrier Name") || "",
        Caller_Type: getValueByLabel("Caller type") || "",
        Category: query || "",
        Issue_Type: getValueByLabel("Chat Issue Type") || "",
        Policy: getValueByLabel("Policy ID") || "",
        AgentId: isAgentPortal() ? getValueByLabel("Agent ID") || "" : "",
        AlternateAgentId: isAgentPortal()
          ? getValueByLabel("Alt Agent ID") || ""
          : "",
        Agent_Email: isAgentPortal()
          ? getValueByLabel("Agent Email") || ""
          : getValueByLabel("Contact Email") || "",
        Caller_Name: getValueByLabel("Caller Name") || "",
        Old_Policy: getValueByLabel("Old Policy ID") || "",
        Portal_Chat_Id: getValueByLabel("Portal Chat ID") || "",
        Line_Of_Business: productType || "",
        RoutingQueueName: routingQueueName || "",
      });
    });

    window.addEventListener(
      "onEmbeddedMessagingConversationStarted",
      (event) => {
        console.log("Conversation started:", event.detail?.conversationId);
        setHideCustomChat(true);
        setConnectingToSF(false);
      },
      { once: true }
    );

    window.addEventListener("onEmbeddedMessagingButtonCreated", () => {
      window.embeddedservice_bootstrap.utilAPI.launchChat();
      setHideCustomChat(true);
      setConnectingToSF(false);
    });

    window.addEventListener(
      "onEmbeddedMessagingConversationClosed",
      () => {
        if (config.ENABLE_POST_CHAT_SURVEY === "true") {
          window.open(`/survey?uuid=${UUID}`);
        }
        setHideCustomChat(false);
        setConnectingToSF(false);
        setOpenChatWindow(false);
        window.location.reload();
      },
      { once: true }
    );
  };

  const submitChatQuestionaires = async (data) => {
    if (formStep === 1) {
      const isValid = await trigger([
        "mainCategory",
        "subCategory",
        "policyNumber",
      ]);
      if (isValid) {
        if (
          // in customer portal, if selected policy is annuity type, redirect to annuity specific queue for SFDC
          !isAgentPortal() &&
          JSON.parse(data.policyNumber)?.productType?.toLowerCase() ===
            "annuity"
        ) {
          const annuitySpecificCategory =
            customerChatMainCategoriesAnnuity.find(
              (category) => category.id === data.mainCategory.option.id
            );

          setSFButtonId(annuitySpecificCategory.buttonId);
          setSFDCName(annuitySpecificCategory.sfdcName);
          setSFDCESWLiveAgentDevName(
            annuitySpecificCategory.sfdcESWLiveAgentDevName
          );
        }
        setFormStep(2);
      }
    }
    if (formStep === 2) {
      const isValid = await trigger("message");
      if (isValid) {
        setConnectingToSF(true);

        const prechatInfo = isAgentPortal()
          ? getAgentPrechatInfo(
              UUID,
              user,
              selectedMainCategory,
              subCategories,
              data
            )
          : getCustomerPrechatInfo(
              UUID,
              user,
              selectedMainCategory,
              subCategories,
              data
            );

        const getValueByLabel = (label) =>
          prechatInfo?.extraPrechatFormDetails?.find(
            (item) => item.label === label
          )?.value || "";

        const ROUTING_QUEUE_MAP = {
          Agent: {
            Life: {
              "Billing & Payments": "EVL_Billing_Payments_Agent_Life",
              Claims: "EVL_Claims_Agent_Life",
              Commissions: "EVL_Commissions_Agent_Life",
              Disbursements: "EVL_Disbursements_Agent_Life",
              Escalation: "EVL_Escalation",
              Illustrations: "EVL_Illustrations_Agent_Life",
              "Something Else": "EVL_Something_Else_Agent_Life",
              "Contract Changes": "EVL_Contract_Changes_Agent_Life",
              "Contract Information": "EVL_Contract_Information_Agent_Life",
              "Technical Assistance": "EVL_Technical_Assistance_Agent",
            },
            Annuity: {
              "Billing & Payments": "EVL_Billing_Payments_Agent_Annuity",
              Claims: "EVL_Claims_Agent_Annuity",
              Commissions: "EVL_Commissions_Agent_Annuity",
              Disbursements: "EVL_Disbursements_Agent_Annuity",
              Escalation: "EVL_Escalation",
              Illustrations: "EVL_Illustrations_Agent_Annuity",
              "Something Else": "EVL_Something_Else_Agent_Annuity",
              "Contract Changes": "EVL_Contract_Changes_Agent_Annuity",
              "Contract Information": "EVL_Contract_Information_Agent_Annuity",
              "Technical Assistance": "EVL_Technical_Assistance_Agent",
            },
          },
          Customer: {
            Life: {
              "Billing & Payments": "EVL_Billing_Payments_Customer_Life",
              Claims: "EVL_Claims_Customer_Life",
              Commissions: "EVL_Commissions_Customer_Life",
              Disbursements: "EVL_Disbursements_Customer_Life",
              Escalation: "EVL_Escalation",
              Illustrations: "EVL_Illustrations_Customer_Life",
              "Something Else": "EVL_Something_Else_Customer_Life",
              "Contract Changes": "EVL_Contract_Changes_Customer_Life",
              "Contract Information": "EVL_Contract_Information_Customer_Life",
              "Technical Assistance": "EVL_Technical_Assistance_Customer",
            },
            Annuity: {
              "Billing & Payments": "EVL_Billing_Payments_Customer_Annuity",
              Claims: "CRM_EVL_Customer_Claims_Annuity",
              Commissions: "EVL_Commissions_Customer_Annuity",
              Disbursements: "EVL_Disbursements_Customer_Annuity",
              Escalation: "EVL_Escalation",
              Illustrations: "EVL_Illustrations_Customer_Annuity",
              "Something Else": "EVL_Something_Else_Customer_Annuity",
              "Contract Changes": "EVL_Contract_Changes_Customer_Annuity",
              "Contract Information":
                "EVL_Contract_Information_Customer_Annuit",
              "Technical Assistance": "EVL_Technical_Assistance_Customer",
            },
          },
        };

        let productType = JSON.parse(data.policyNumber)?.productType;
        if (!productType) {
          productType = "Life";
        }
        const query = getValueByLabel("Chat Category");
        const portalType = isAgentPortal() ? "Agent" : "Customer";
        const routingQueueName =
          ROUTING_QUEUE_MAP?.[portalType]?.[productType]?.[query];

        if (!window.embeddedservice_bootstrap) {
          const s = document.createElement("script");
          s.src =
            config.SFDC_VERSION === "PROD"
              ? "https://nttfsiclaims.my.site.com/ESWEVLChatSupport1770287966136/assets/js/bootstrap.min.js"
              : config.SFDC_VERSION === "UAT"
              ? "https://nttfsiclaims--nttfsiuat.sandbox.my.site.com/ESWEVLChatSupport1769583709310/assets/js/bootstrap.min.js"
              : "https://nttfsiclaims--fsitagqa.sandbox.my.site.com/ESWEVLChatAgentChannel1768226837707/assets/js/bootstrap.min.js";
          s.onload = () =>
            initESW(null, {
              getValueByLabel,
              query,
              productType,
              routingQueueName,
            });
          document.body.appendChild(s);
        } else {
          initESW("https://service.force.com", {
            getValueByLabel,
            query,
            productType,
            routingQueueName,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!openChatWindow) {
      setFormStep(1);
      setSelectedMainCategory(t("AuthChatFormWindow-option-1"));
      reset();
    }
  }, [openChatWindow]);

  // prepopulate the autocomplete list
  useEffect(() => {
    const acPolicyIds = policies.map((policy) => {
      const primaryOwnerInfo = getPrimaryOwnerFromParties(policy?.parties);

      return {
        value: JSON.stringify({
          oldPolicy: policy.oldPolicy,
          contractId: policy.contractID,
          // needed for customer portal queue redirect
          productType: getProductLineByPolicyType(policy?.product?.type),
        }),
        displayAs: `${policy?.oldPolicy} - ${getName(
          primaryOwnerInfo?.person
        )} - ${getPolicyType(policy)}`,
      };
    });

    if (isAgentPortal()) {
      let policySelectionArray = [];
      policies?.map((policy) => {
        policySelectionArray.push({
          value: policy?.oldPolicy,
          displayAs: `${policy?.oldPolicy}: ${policy?.insuredFullName}`,
          displayAsValue: `${policy?.oldPolicy}`,
          prodType: `${getProductLineByPolicyType(policy?.product?.type)}`,
          rawProductType: policy?.product?.type,
          ...policy,
        });
      });

      if (policySelectionArray?.length > 0)
        setPolicySelection([
          {
            value: "n/a",
            displayAs: "Not Applicable",
            displayAsValue: "Not Applicable",
          },
          ...policySelectionArray,
        ]);
    } else {
      // empty value needed to trigger required field for form
      // removed "Not Applicable" for customer portal
      const policySelectionArray = getPoliciesOptions(policies);
      let policiesOptions = [];

      policySelectionArray.map((policy) => {
        policiesOptions.push({
          ...policy,
          selectedPolicyDisplayAs: `${policy.value}`,
          rawProductType: policy?.policyData?.product?.type,
        });
      });
      setPolicySelection([selectedPolicyId, ...policiesOptions]);

      // default customer portal policyID if there are only one
      if (policiesOptions.length === 1) setSelectedPolicyId(policiesOptions[0]);
    }
  }, []);

  // manual trigger validation onSelect
  useEffect(() => {
    if (selectedPolicyId?.value && selectedPolicyId?.value !== "all") {
      const selectedPolicy = JSON.stringify({
        oldPolicy: selectedPolicyId?.value,
        productType: selectedPolicyId?.prodType,
        contractId: selectedPolicyId?.contractID,
        rawProductType: selectedPolicyId?.rawProductType,
      });
      setValue("policyNumber", selectedPolicy);
      clearErrors("policyNumber");
    }
  }, [selectedPolicyId]);

  useEffect(() => {
    if (selectedMainCategory === "Something Else") setValue("subCategory", "0");
  }, [selectedMainCategory]);

  const selectedPolicy = parsePolicy(policyNumber);
  const isRestricted = formStep === 2 && isRestrictedPolicy(selectedPolicy);

  return (
    <div
      className={`ui-card fixed bottom-2 right-6 block shadow-sm rounded-lg z-[500] w-${
        isAgentPortal() ? "[320px]" : "[350px]"
      }`}
    >
      <EverlakeLoadingOverlay
        isLoading={connectingToSF}
        loadingText={
          formStep === 1
            ? t("AuthChatFormWindow-loading-text-1")
            : t("AuthChatFormWindow-loading-text-2")
        }
      >
        <div className="h-[50px] bg-ui-blue text-white flex justify-between items-center text-lg rounded-t-lg">
          <div className="flex flex-row px-4">
            {config.COMPANY_NAME === "everlake" && (
              <img
                className="w-7 h-7 mr-2"
                src={getImage("symbol_white.png")}
                alt={t("AuthChatFormWindow-alt-text-1")}
              />
            )}

            <h3 className="font-semibold">{t("AuthChatFormWindow-title")}</h3>
          </div>
          <div className="px-2">
            <button
              className="hover:bg-white/30 hover:rounded-lg p-2"
              aria-label={t("AuthChatFormWindow-aria-label-1")}
              onClick={() => setOpenChatWindow(false)}
            >
              <svg
                className="ui-icon h-5 w-5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-minus"></use>
              </svg>
            </button>
            <button
              className="hover:bg-white/30 hover:rounded-lg p-2 ml-0"
              aria-label={t("AuthChatFormWindow-aria-label-2")}
              onClick={() => setOpenChatWindow(false)}
            >
              <svg
                className="ui-icon h-5 w-5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
          </div>
        </div>
        <div className="flex flex-col py-5 px-8 overflow-auto h-[450px]">
          {formStep === 1 && (
            <>
              <p className="text-center text-sm">
                {t("AuthChatFormWindow-form-text-1")}
              </p>
              <div className="flex">
                <hr className="w-[45%]" />
                <svg
                  className="ui-icon h-5 w-[10%] self-center text-ui-blue-dark"
                  focusable="false"
                  aria-hidden="true"
                  role="presentation"
                >
                  <use href="#ui-icon-chat-bubble"></use>
                </svg>
                <hr className="w-[45%]" />
              </div>
            </>
          )}

          <form
            onSubmit={handleSubmit(submitChatQuestionaires)}
            className="flex flex-col justify-between h-full"
          >
            <div className="flex flex-col gap-y-4">
              {formStep === 1 && (
                <>
                  <div>
                    {isAgentPortal() ? (
                      <Controller
                        name="mainCategory"
                        control={control}
                        render={() => (
                          <AgentAutoCompleteComboBox
                            labelName={t("AuthChatFormWindow-label-1")}
                            inputPlaceholderText={t(
                              "AuthChatFormWindow-option-1"
                            )}
                            options={[
                              {
                                value: "all",
                                displayAs: t("AuthChatFormWindow-option-1"),
                                displayAsValue: t(
                                  "AuthChatFormWindow-option-1"
                                ),
                                selectedPolicyDisplayAs: t(
                                  "AuthChatFormWindow-option-1"
                                ),
                              },
                              ...mainCategories.map((category) => ({
                                value: category.name,
                                displayAs: category.name,
                                displayAsValue: category.name,
                                selectedPolicyDisplayAs: category.name,
                                isDisplayOnly: true,
                                ...category,
                              })),
                            ]}
                            useSmallFont={true}
                            selectedOption={selectedMainCategory}
                            setSelectedOption={(option) => {
                              if (option?.value !== "all") {
                                clearErrors("mainCategory");
                                setValue("mainCategory", { option });
                                setSelectedMainCategory(option.value);
                                setSelectedSubCategory("all");
                                setSFButtonId(option.buttonId);
                                setSFDCName(option.sfdcName);
                                setSFDCESWLiveAgentDevName(
                                  option.sfdcESWLiveAgentDevName
                                );
                              } else {
                                setError("mainCategory", {
                                  message: t(
                                    "AuthChatFormWindow-error-message-1"
                                  ),
                                });
                                setValue("mainCategory", "");
                                setValue("subCategory", "");
                                setSelectedMainCategory("");
                              }
                            }}
                          />
                        )}
                        rules={{
                          required: t("AuthChatFormWindow-error-message-1"),
                        }}
                      />
                    ) : (
                      <Controller
                        name="mainCategory"
                        control={control}
                        render={() => (
                          <RELEASING_AutoCompleteComboBox
                            labelName={t("AuthChatFormWindow-label-1")}
                            inputPlaceholderText={t(
                              "AuthChatFormWindow-option-1"
                            )}
                            options={[
                              {
                                value: "all",
                                displayAs: t("AuthChatFormWindow-option-1"),
                                selectedPolicyDisplayAs: t(
                                  "AuthChatFormWindow-option-1"
                                ),
                              },
                              ...mainCategories.map((category) => ({
                                value: category.name,
                                displayAs: category.name,
                                selectedPolicyDisplayAs: category.name,
                                isDisplayOnly: true,
                                ...category,
                              })),
                            ]}
                            useSmallFont={true}
                            selectedOption={selectedMainCategory}
                            setSelectedOption={(option) => {
                              if (option?.value !== "all") {
                                clearErrors("mainCategory");
                                setValue("mainCategory", { option });
                                setSelectedMainCategory(option.value);
                                setSelectedSubCategory("all");
                                setSFButtonId(option.buttonId);
                                setSFDCName(option.sfdcName);
                                setSFDCESWLiveAgentDevName(
                                  option.sfdcESWLiveAgentDevName
                                );
                              } else {
                                setError("mainCategory", {
                                  message: t(
                                    "AuthChatFormWindow-error-message-1"
                                  ),
                                });
                                setValue("mainCategory", "");
                                setValue("subCategory", "");
                                setSelectedMainCategory(option.value);
                              }
                            }}
                          />
                        )}
                        rules={{
                          required: t("AuthChatFormWindow-error-message-1"),
                        }}
                      />
                    )}
                    <FormInputError
                      id="chat-main-category"
                      errorMessage={errors?.mainCategory?.message}
                    />
                  </div>

                  {selectedMainCategory &&
                    subCategories[selectedMainCategory]?.length > 0 &&
                    selectedMainCategory !== "Something Else" && (
                      <div>
                        {isAgentPortal() ? (
                          <Controller
                            name="subCategory"
                            control={control}
                            render={() => (
                              <AgentAutoCompleteComboBox
                                labelName={
                                  <Trans
                                    defaults={t("AuthChatFormWindow-label-2")}
                                    values={{
                                      category:
                                        selectedMainCategory.toLowerCase(),
                                    }}
                                  />
                                }
                                inputPlaceholderText={t(
                                  "AuthChatFormWindow-option-2"
                                )}
                                options={[
                                  {
                                    value: "all",
                                    displayAs: t("AuthChatFormWindow-option-2"),
                                    displayAsValue: t(
                                      "AuthChatFormWindow-option-2"
                                    ),
                                    selectedPolicyDisplayAs: t(
                                      "AuthChatFormWindow-option-2"
                                    ),
                                  },
                                  ...subCategories[selectedMainCategory].map(
                                    (category) => ({
                                      value: category.name,
                                      displayAs: category.name,
                                      displayAsValue: category.name,
                                      selectedPolicyDisplayAs: category.name,
                                      id: category.id,
                                      isDisplayOnly: true,
                                    })
                                  ),
                                ]}
                                useSmallFont={true}
                                selectedOption={selectedSubCategory}
                                isChat={true}
                                setSelectedOption={(option) => {
                                  if (option?.value !== "all") {
                                    clearErrors("subCategory");
                                    setValue("subCategory", option.id);
                                    setSelectedSubCategory(option.value);
                                  } else {
                                    setError("subCategory", {
                                      message: t(
                                        "AuthChatFormWindow-error-message-2"
                                      ),
                                    });
                                    setValue("subCategory", "");
                                    setSelectedSubCategory(option.value);
                                  }
                                }}
                              />
                            )}
                            rules={{
                              required: t("AuthChatFormWindow-error-message-2"),
                            }}
                          />
                        ) : (
                          <div>
                            <Controller
                              name="subCategory"
                              control={control}
                              render={() => (
                                <RELEASING_AutoCompleteComboBox
                                  labelName={
                                    <Trans
                                      defaults={t("AuthChatFormWindow-label-2")}
                                      values={{
                                        category:
                                          selectedMainCategory.toLowerCase(),
                                      }}
                                    />
                                  }
                                  inputPlaceholderText={t(
                                    "AuthChatFormWindow-option-2"
                                  )}
                                  options={[
                                    {
                                      value: "all",
                                      displayAs: t(
                                        "AuthChatFormWindow-option-2"
                                      ),
                                      selectedPolicyDisplayAs: t(
                                        "AuthChatFormWindow-option-2"
                                      ),
                                    },
                                    ...subCategories[selectedMainCategory].map(
                                      (category) => ({
                                        value: category.name,
                                        displayAs: category.name,
                                        selectedPolicyDisplayAs: category.name,
                                        id: category.id,
                                        isDisplayOnly: true,
                                      })
                                    ),
                                  ]}
                                  useSmallFont={true}
                                  selectedOption={selectedSubCategory}
                                  isChat={true}
                                  setSelectedOption={(option) => {
                                    if (option?.value !== "all") {
                                      clearErrors("subCategory");
                                      setValue("subCategory", option.id);
                                      setSelectedSubCategory(option.value);
                                    } else {
                                      setError("subCategory", {
                                        message: t(
                                          "AuthChatFormWindow-error-message-2"
                                        ),
                                      });
                                      setValue("subCategory", "");
                                      setSelectedSubCategory(option.value);
                                    }
                                  }}
                                />
                              )}
                              rules={{
                                required: t(
                                  "AuthChatFormWindow-error-message-2"
                                ),
                              }}
                            />
                          </div>
                        )}
                        <FormInputError
                          id="chat-sub-category"
                          errorMessage={errors?.subCategory?.message}
                        />
                      </div>
                    )}

                  <div>
                    {/* if only one policies, default policy selection */}
                    <Controller
                      name="policyNumber"
                      control={control}
                      render={() =>
                        isAgentPortal() ? (
                          <AgentAutoCompleteComboBox
                            labelName={t("AgentPaymentPolicy-policynumber")}
                            inputPlaceholderText={"Select a contract"}
                            options={policySelection}
                            invalidMessage={"No contract match"}
                            selectedOption={selectedPolicyId}
                            setSelectedOption={setSelectedPolicyId}
                          />
                        ) : (
                          <RELEASING_AutoCompleteComboBox
                            labelName={t(
                              "CustomerAlertsCorrespondence-contract-number"
                            )}
                            inputPlaceholderText={t(
                              "AutoComplate-select-your-contract"
                            )}
                            options={policySelection}
                            invalidMessage="No contract match"
                            selectedOption={selectedPolicyId}
                            setSelectedOption={setSelectedPolicyId}
                            readOnly={policies?.length === 1}
                            useSmallFont={true}
                          />
                        )
                      }
                      rules={{
                        required: `Please select a contract number for related to this chat issue${
                          isAgentPortal()
                            ? ", or you can select 'Not Applicable'"
                            : "."
                        }`,
                      }}
                    />
                    <FormInputError
                      id="chatPolicyNumber-error"
                      errorMessage={errors?.policyNumber?.message}
                    />
                  </div>
                </>
              )}

              {formStep === 2 && (
                <>
                  <h3 className="font-semibold text-2xl align-middle">
                    <button
                      className="ui-btn-hyperlink"
                      type="button"
                      onClick={() => setFormStep(1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 19.5L8.25 12l7.5-7.5"
                        />
                      </svg>
                    </button>
                    {selectedMainCategory}
                  </h3>
                  <div className="w-[80%] mx-auto mb-4">
                    {getValues("subCategory") &&
                      subCategories[selectedMainCategory][
                        getValues("subCategory")
                      ]?.message && (
                        <p className="text-sm">
                          {
                            subCategories[selectedMainCategory][
                              getValues("subCategory")
                            ].message
                          }
                        </p>
                      )}
                  </div>
                </>
              )}

              {formStep === 3 && (
                <div className="self-center">
                  <h3 className="font-semibold text-3xl align-middle text-center">
                    {t("AuthChatFormWindow-step-3-title")}
                  </h3>
                  <br />

                  <div className="mx-auto text-center text-md">
                    <Trans
                      i18nKey="AuthChatFormWindow-step-3-text-1"
                      components={{
                        link1: <Link to="/help"></Link>,
                        link2: <Link to="/help/faqs"></Link>,
                        break: <br />,
                      }}
                    ></Trans>
                  </div>
                </div>
              )}
            </div>

            <div>
              {formStep === 2 && (
                <>
                  {isRestricted ? (
                    <div className="w-[95%] mx-auto mb-4 text-sm">
                      <p className="text-sm">
                        <Trans
                          i18nKey="AuthChatFormWindow-step-2-restricted-message"
                          components={{
                            break: <br />,
                          }}
                        ></Trans>
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="w-[95%] mx-auto mb-4">
                        <h3 className="text-xs font-semibold mb-2">
                          {subCategories[selectedMainCategory][
                            getValues("subCategory")
                          ]?.message && (
                            <>
                              <Trans
                                i18nKey="AuthChatFormWindow-step-2-title-1"
                                components={{
                                  break: <br />,
                                }}
                              ></Trans>
                              <br />
                            </>
                          )}
                          {t("AuthChatFormWindow-step-2-title-2")}
                        </h3>

                        <textarea
                          className="resize-none	"
                          type="text"
                          rows="2"
                          maxLength={200}
                          {...register("message", {
                            required: t(
                              "AuthChatFormWindow-step-2-error-text-1"
                            ),
                            minLength: {
                              value: 10,
                              message: t(
                                "AuthChatFormWindow-step-2-error-text-2"
                              ),
                            },
                            maxLength: {
                              value: 200,
                            },
                          })}
                        />
                      </div>
                      <FormInputError errorMessage={errors?.message?.message} />
                    </>
                  )}
                </>
              )}
              <button
                className="ui-btn-primary w-full h-10 mt-4"
                disabled={isRestricted}
                onClick={() => {
                  if (formStep === 3) {
                    setOpenChatWindow(false);
                  }
                }}
              >
                {formStep === 1 && "Next"}
                {formStep === 2 && "Start Chat"}
                {formStep === 3 && "Close Chat"}
              </button>

              <p className="text-xs mt-[5px]">
                {t("AuthChatFormWindow-step-2-text-1")}
              </p>
            </div>
          </form>
        </div>
      </EverlakeLoadingOverlay>
    </div>
  );
}
